import { $document } from '@umu/umu-dom';
import { getCurrentSystem, isLang } from '@umu/ukit-lang';
import { isDingding, isDingdingDesktop, isFeishu, isInMpDesktop } from '@umu/umu-ua';
import { USEQuery } from './use';

export * from '@umu/ukit-lang';
export * from './@datetime';
export * from './defaultAvatars';
export * from './number';
export * from './string';
export * from './log';
export * from './object';
export * from './regexp';
export * from './array';
export * from './file';
export * from './animation';
export * from './deferred';
export * from './eventEmitterWrapper';
export * from './keyboard';
export * from './crypto';
export * from './rgbaster';
export * from './interface';
export * from './umu';
export * from './dom-to-image';
export * from './designConfig/datePicker';
export * from './designConfig/select';
export * from './currency';
export * from './violation';
export * from './umuStatistic';

export * from './rtl';
export * from './use';
import { checkIsAuthCDNUrl } from './cdn';
export { getTopLevelWindow } from './microApp';

export { default as ScopeLangStore } from './scopeLang';
export { checkIsAuthCDNUrl };

/**
 * @desc
 * @param
 */
export interface ICancelableFunction {
    (...args: any[]): any;
    cancel: () => void;
    [props: string]: any;
}

/**
 * 兼容不是 JSON 格式的字符串，不是 JSON 返回 false
 * @param str String
 * @returns Object | false
 */
export const jsonParse = (str: string) => {
    let data: object | false = {};

    try {
        data = JSON.parse(str);
    } catch (error) {
        console.error('jsonParse failed', error, str);
        data = false;
    }
    return data;
};

/**
 * debounce
 * via: screen
 */
export const debounce = (
    func: (...args: any[]) => any,
    wait: number,
    context?: any,
    immediate = false,
): ((...args: any[]) => any) => {
    context = context || null;
    let timeout: number | null;
    return (...args: any[]) => {
        const later = () => {
            timeout = null;
            if (!immediate) {
                func.call(context, ...args);
            }
        };
        const callNow = immediate && !timeout;
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = window.setTimeout(later, wait);
        if (callNow) {
            func.call(context, ...args);
        }
    };
};

/**
 * throttle
 */
export const throttle = (fn: (...args: any[]) => any, wait: number, context?: any) => {
    let nextFn: number | any;
    let lastTimer: number;
    const newFunction = ((...args: any[]) => {
        if (!lastTimer) {
            fn.apply(context, args);
            lastTimer = Date.now();
        } else {
            clearTimeout(nextFn);
            nextFn = setTimeout(() => {
                if (Date.now() - lastTimer >= wait) {
                    fn.apply(context, args);
                    lastTimer = Date.now();
                }
            }, wait - (Date.now() - lastTimer));
        }
    }) as ICancelableFunction;

    newFunction.cancel = () => {
        clearTimeout(nextFn);
    };
    return newFunction;
};

/**
 * 与 throttle 的区别是，保证最后一次一定会执行
 */
export function throttleGuarantee<T extends any[]>(func: (...args: T) => void, interval: number): (...args: T) => void {
    let timer: NodeJS.Timeout | null = null;
    let previous = 0;
    let lastArgs: T | null = null;

    return function (...args: T) {
        const now = Date.now();
        const remaining = interval - (now - previous);

        lastArgs = args;

        if (remaining <= 0) {
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }

            func(...args);
            previous = now;
        } else if (!timer) {
            timer = setTimeout(() => {
                if (lastArgs) {
                    func(...lastArgs);
                    lastArgs = null;
                }
                timer = null;
                previous = Date.now();
            }, remaining);
        }
    };
}

function isObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export const getValue = (obj: any, keys: string) => {
    let val = null;
    const keyArr = keys.split('.');
    keyArr.reduce((cur: any, key: string, index) => {
        if (index === keyArr.length - 1) {
            val = isObject(cur) ? cur[key] : undefined;
        }
        return isObject(cur) ? cur[key] : undefined;
    }, obj);
    return val;
};

/**
 * @desc
 * @param
 */
export function interval(fn: (...args: any[]) => any, wait: number, context?: any) {
    let timer: number | null;
    const newFunction = ((...args: any[]) => {
        newFunction.cancel();
        timer = window.setInterval(() => {
            fn.apply(context, args);
        }, wait);
    }) as ICancelableFunction;
    newFunction.cancel = () => {
        if (timer) {
            clearInterval(timer);
        }
        timer = null;
    };
    return newFunction;
}

/**
 * @desc
 * @param
 */
export function once(fn: (...args: any[]) => any, context?: any) {
    const newFunc = (...args: any[]) => {
        const result = fn.apply(context, args);
        fn = () => void 0;
        return result;
    };
    return newFunc;
}

let idCounter = 0;
/**
 * @desc
 * @param
 */
export function getUniqueId(prefix: string = '') {
    const id = idCounter++;
    return (prefix ? prefix + id : id) + '';
}

/**
 * @desc
 * @param
 */
export function parseUrl(url: string) {
    const l = document.createElement('a');
    l.href = url;
    return l;
}

/**
 * @desc
 * @param
 */
export function cancelableTimeout(callback: (...args: any[]) => void, wait: number, context?: any) {
    let timer: number;
    const newFunction = ((...args: any[]) => {
        newFunction.cancel();
        timer = window.setTimeout(() => {
            callback.apply(context, args);
        }, wait);
    }) as ICancelableFunction;
    newFunction.cancel = () => {
        clearTimeout(timer);
    };
    return newFunction;
}

export function getCDNFileUsingVanillaFetch(url: string) {
    const fetchOptions: RequestInit = {};
    if (checkIsAuthCDNUrl(url)) {
        fetchOptions['credentials'] = 'include';
    }
    return fetch(httpToHttps(url), fetchOptions).then((response) => response.json());
}

export async function downloadCDNImageUsingVanillaFetch(url: string, name?: string) {
    const fetchOptions: RequestInit = {};
    if (checkIsAuthCDNUrl(url)) {
        fetchOptions['credentials'] = 'include';
    }

    const element = document.createElement('a');
    const imgBlob = await fetch(url, fetchOptions).then((res) => res.blob());
    const blobUrl = URL.createObjectURL(imgBlob);
    element.setAttribute('href', blobUrl);
    element.setAttribute('target', '_blank');
    element.setAttribute('download', name || '');
    document.body.appendChild(element);
    element.click();
    element.remove();
}

/**
 * @desc
 * @param
 */
export function getGuid() {
    return Date.now() + Math.floor(Math.random() * 1000) + '';
}

/**
 * 通过点击a标签的方式打开窗口
 * @param {*} url 完整的url
 * @param {*} target 以何种方式打开 '_blank' | '_self'
 * @param {*} openType a-link | window, default a-link
 * @author Kody
 */
export const openWindow = (
    url: string,
    target: '_blank' | '_self' = '_blank',
    openType: 'a-link' | 'window' = 'a-link',
    args?: any,
): any => {
    // @see https://app.asana.com/0/1203622320206279/1203645972204374
    // 桌面版钉钉强制打开当前页面
    let realTarget = target;
    if (isDingding || isDingdingDesktop || isFeishu) {
        realTarget = '_self';
    }
    if (openType === 'window') {
        return window.open(url, realTarget, args);
    } else {
        // todo 下面这句不该放在这里，用什么url不是openwindow的职责
        url = /^http/.test(url) ? url : `https://${url}`;
        const alink = $document.createElement('a');
        let newUrl: URL;
        try {
            newUrl = new URL(url);
        } catch (_) {
            newUrl = new URL(url, window.location.href);
        }
        if ((window as any).isInUSE) {
            newUrl.searchParams.append(USEQuery, '1');
        }
        if (isInMpDesktop) {
            window.location.href = newUrl.toString();
            return;
        }
        alink.href = newUrl.toString();
        alink.target = realTarget;
        alink.rel = 'noopener';
        alink.click();
        if (alink.remove) {
            alink.remove();
        }
    }
};

type anyFnc = (text: any) => any;
export function compose(_arg1: anyFnc, _arg2?: anyFnc, _arg3?: anyFnc): anyFnc {
    const _len = arguments.length;
    const funcs = Array(_len);
    for (let _key = 0; _key < _len; _key++) {
        funcs[_key] = arguments[_key];
    }

    // tslint:disable-next-line:only-arrow-functions
    return function () {
        if (funcs.length === 0) {
            return arguments.length <= 0 ? undefined : arguments[0];
        }

        const last = funcs[funcs.length - 1];
        const rest = funcs.slice(0, -1);

        return rest.reduceRight((composed, f) => {
            return f(composed);
        }, last.apply(undefined, arguments));
    };
}

declare global {
    interface Document {
        fullscreenElement: Element;
        webkitFullscreenElement?: Element;
        msFullscreenElement?: Element;
        mozFullscreenElement?: Element;
    }
}

export const checkIsFullscreen = () => {
    const el = document;
    return !!(el.fullscreenElement || el.webkitFullscreenElement || el.msFullscreenElement || el.mozFullscreenElement);
};

/**
 * 下载兼容https
 */
export const httpToHttps = (url: string) => {
    if (!url) {
        return 'javascript:;';
    }
    if (url.startsWith('http://')) {
        url = url.replace('http://', 'https://');
    }
    return url;
};

const system: string = getCurrentSystem(); // CN/EN/JP/TW/EM
const orientalAreaArr: string[] = ['CN', 'TW', 'JP'];
/**
 * @desc 是不是东方国家
 */
export const isOrientalArea = orientalAreaArr.indexOf(system) !== -1;

/** 整合多个class名 */
export function classes(...args: any[]): string {
    return [...args].filter((item) => !!item).join(' ');
}

// 一句话只有第一个单词首字母大写，其它单词全部小写
export function textTransformToCapital(text: string) {
    if (!isLang('en')) {
        return text;
    }
    if (typeof text !== 'string') {
        return text;
    }

    const words = text.split(' ');
    return words
        .map((word, index) => {
            if (index === 0) {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            }
            return word.toLowerCase();
        })
        .join(' ');
}

// 一句话每个单词首字母大写，其它字母小写
export function everyTextTransformToCapital(text: string) {
    if (!isLang('en')) {
        return text;
    }
    if (typeof text !== 'string') {
        return text;
    }

    const words = text.split(' ');
    return words
        .map((word) => {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
}

// check a string is already been encoded by encodeURIComponent
export function checkIsURLEncoded(str: string) {
    if (!str || typeof str !== 'string') {
        return false;
    }

    // 检查是否有非法字符
    if (/[\u0000-\u001F\u007F-\u009F]/.test(str)) {
        return false;
    }

    try {
        const decoded = decodeURIComponent(str);
        // 如果解码后和原字符串相同，且原字符串包含 %
        // 则可能是未编码的包含 % 的字符串
        if (decoded === str && str.includes('%')) {
            return false;
        }
        return decoded !== str;
    } catch (e) {
        return false;
    }
}
